import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { MemberAuthProvider, useMemberAuth } from './contexts/MemberAuthContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { firstAllowedPath } from './utils/permissions';

import MainLayout from './components/Layout/MainLayout';
import MemberLayout from './components/Layout/MemberLayout';
import Login from './pages/Login';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './components/Dashboard/Dashboard';

// Public website
const WebLayout = React.lazy(() => import('./pages/website/WebLayout'));
const HomePage = React.lazy(() => import('./pages/website/HomePage'));
const StandingsPage = React.lazy(() => import('./pages/website/StandingsPage'));
const AboutPage = React.lazy(() => import('./pages/website/AboutPage'));
const EventsPage = React.lazy(() => import('./pages/website/EventsPage'));
const PackagesPage = React.lazy(() => import('./pages/website/PackagesPage'));
const ContactPage = React.lazy(() => import('./pages/website/ContactPage'));
const SponsorsPage = React.lazy(() => import('./pages/website/SponsorsPage'));
const LegalPage = React.lazy(() => import('./pages/website/LegalPage'));

// Lazy load other pages
const RegistrationForm = React.lazy(() => import('./components/Public/RegistrationForm'));
const QRScannerPage = React.lazy(() => import('./components/QRScanner/QRScannerPage'));
const MemberList = React.lazy(() => import('./components/Members/MemberList'));
const MemberDetail = React.lazy(() => import('./components/Members/MemberDetail'));
const ApplicationList = React.lazy(() => import('./components/Members/ApplicationList'));
const DuesOverview = React.lazy(() => import('./components/Dues/DuesOverview'));
const PaymentsList = React.lazy(() => import('./components/Payments/PaymentsList'));
const NotificationLog = React.lazy(() => import('./components/Notifications/NotificationLog'));
const EntryLog = React.lazy(() => import('./components/Entry/EntryLog'));
const AnnouncementList = React.lazy(() => import('./components/Announcements/AnnouncementList'));
const Reports = React.lazy(() => import('./components/Reports/Reports'));
const Settings = React.lazy(() => import('./components/Settings/Settings'));
const WebsiteManager = React.lazy(() => import('./components/Website/WebsiteManager'));
const Profile = React.lazy(() => import('./pages/Profile'));

// Member portal pages (Login: tek/birlesik giris "Login" sayfasi ile /login uzerinden)
const MemberDashboard = React.lazy(() => import('./pages/member/MemberDashboard'));
const MemberDues = React.lazy(() => import('./pages/member/MemberDues'));
const MemberPayment = React.lazy(() => import('./pages/member/MemberPayment'));
const MemberProfile = React.lazy(() => import('./pages/member/MemberProfile'));
const MemberEntries = React.lazy(() => import('./pages/member/MemberEntries'));

function MemberProtectedRoute({ children }) {
  const { member, loading } = useMemberAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  if (!member) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

function ProtectedRoute({ children, requiredRole, requiredPermission }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (requiredPermission && user.role !== 'baskan') {
    if (!user.permissions || !user.permissions[requiredPermission]) {
      // Yetki yoksa erisebildigi ilk sayfaya (dashboard == /admin oldugu icin loop olmasin)
      return <Navigate to={firstAllowedPath(user)} replace />;
    }
  }

  if (requiredRole) {
    const roleHierarchy = { gorevli: 0, yonetici: 1, baskan: 2 };
    if ((roleHierarchy[user.role] || 0) < (roleHierarchy[requiredRole] || 0)) {
      return <Navigate to={firstAllowedPath(user)} replace />;
    }
  }

  return children;
}

function AppRoutes() {
  return (
    <React.Suspense
      fallback={
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      }
    >
      <Routes>
        {/* Public website */}
        <Route element={<WebLayout />}>
          <Route index element={<HomePage />} />
          <Route path="hakkimizda" element={<AboutPage />} />
          <Route path="etkinlikler" element={<EventsPage />} />
          <Route path="paketler" element={<PackagesPage />} />
          <Route path="lig-durumu" element={<StandingsPage />} />
          <Route path="iletisim" element={<ContactPage />} />
          <Route path="sponsorlar" element={<SponsorsPage />} />
          <Route path="impressum" element={<LegalPage page="impressum" />} />
          <Route path="datenschutz" element={<LegalPage page="datenschutz" />} />
        </Route>

        {/* Public routes - tüm giriş yönlendirmeleri tek /login üzerinden */}
        <Route path="/giris" element={<Navigate to="/login" replace />} />
        <Route path="/login" element={<Login />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/uye/giris" element={<Navigate to="/login" replace />} />
        <Route path="/kayit" element={<RegistrationForm />} />

        {/* Protected routes - Admin Dashboard */}
        <Route
          path="/admin"
          element={
            <ProtectedRoute requiredPermission="dashboard">
              <MainLayout>
                <Dashboard />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/tarayici"
          element={
            <ProtectedRoute>
              <MainLayout>
                <QRScannerPage />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/uyeler"
          element={
            <ProtectedRoute>
              <MainLayout>
                <MemberList />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/uyeler/:id"
          element={
            <ProtectedRoute>
              <MainLayout>
                <MemberDetail />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/basvurular"
          element={
            <ProtectedRoute>
              <MainLayout>
                <ApplicationList />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/aidatlar"
          element={
            <ProtectedRoute>
              <MainLayout>
                <DuesOverview />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/odemeler"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PaymentsList />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/versandprotokoll"
          element={
            <ProtectedRoute requiredPermission="odemeler">
              <MainLayout>
                <NotificationLog />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/giris-kayitlari"
          element={
            <ProtectedRoute>
              <MainLayout>
                <EntryLog />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/duyurular"
          element={
            <ProtectedRoute>
              <MainLayout>
                <AnnouncementList />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/raporlar"
          element={
            <ProtectedRoute requiredRole="yonetici">
              <MainLayout>
                <Reports />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/website-yonetimi"
          element={
            <ProtectedRoute requiredPermission="website">
              <MainLayout>
                <WebsiteManager />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/ayarlar"
          element={
            <ProtectedRoute requiredPermission="ayarlar">
              <MainLayout>
                <Settings />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/profil"
          element={
            <ProtectedRoute>
              <MainLayout>
                <Profile />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* Member Portal Routes */}
        <Route path="/uye/giris" element={<Navigate to="/login" replace />} />
        <Route
          path="/uye"
          element={
            <MemberProtectedRoute>
              <MemberLayout>
                <MemberDashboard />
              </MemberLayout>
            </MemberProtectedRoute>
          }
        />
        <Route
          path="/uye/aidatlar"
          element={
            <MemberProtectedRoute>
              <MemberLayout>
                <MemberDues />
              </MemberLayout>
            </MemberProtectedRoute>
          }
        />
        <Route
          path="/uye/odeme"
          element={
            <MemberProtectedRoute>
              <MemberLayout>
                <MemberPayment />
              </MemberLayout>
            </MemberProtectedRoute>
          }
        />
        <Route
          path="/uye/profil"
          element={
            <MemberProtectedRoute>
              <MemberLayout>
                <MemberProfile />
              </MemberLayout>
            </MemberProtectedRoute>
          }
        />
        <Route
          path="/uye/giris-kayitlari"
          element={
            <MemberProtectedRoute>
              <MemberLayout>
                <MemberEntries />
              </MemberLayout>
            </MemberProtectedRoute>
          }
        />

        {/* Catch all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </React.Suspense>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <LanguageProvider>
        <AuthProvider>
          <MemberAuthProvider>
            <Toaster
              position="top-right"
              toastOptions={{
                duration: 3000,
                style: { borderRadius: '8px', background: '#333', color: '#fff' },
              }}
            />
            <AppRoutes />
          </MemberAuthProvider>
        </AuthProvider>
      </LanguageProvider>
    </BrowserRouter>
  );
}
